// Route-level role gate. Wrap any authed route element with
// <RoleRoute tab="pharmacy">…</RoleRoute> — the route renders only if the
// signed-in member's role has access to that tab. Off-role → redirected to
// the role's landing path (getRolePermissions().landing).
//
// Reads the same ['my-clinic'] React Query cache that ClinicGate populates,
// so no extra network call: by the time any authed route mounts the query
// has already resolved.

import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { clinicApi } from '../lib/api';
import { getRolePermissions, type ClinicTab } from '../lib/permissions';

export function RoleRoute({ tab, children }: { tab: ClinicTab; children: ReactNode }) {
    const { data } = useQuery({
        queryKey: ['my-clinic'],
        queryFn: () => clinicApi.myClinic(),
    });
    const member = data?.member ?? null;
    if (!member) {
        // ClinicGate should have already caught this; belt-and-braces
        // fallback keeps the tree from rendering an off-role page in a
        // rare race where the cache expired mid-navigation.
        return <Navigate to="/join-clinic" replace />;
    }
    const perms = getRolePermissions(member.role);
    if (!perms.tabs.includes(tab)) {
        return <Navigate to={perms.landing} replace />;
    }
    return <>{children}</>;
}

// Fallback for unknown paths — sends the user to their role's landing tab.
// Used at the `*` catch-all in App.tsx to replace the old hard-coded
// /work-list redirect.
export function RoleLandingRedirect() {
    const { data } = useQuery({
        queryKey: ['my-clinic'],
        queryFn: () => clinicApi.myClinic(),
    });
    const member = data?.member ?? null;
    if (!member) return <Navigate to="/join-clinic" replace />;
    return <Navigate to={getRolePermissions(member.role).landing} replace />;
}
